import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import rmsActions from "../../../../actions/rmsActions";
import { getActivities, getRmsAppCode, isFetching } from "../../../../reducers";
import { DataTypes } from "../../../../constants";

import { Grid, Container } from "@mui/material";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import FabButton from "../../../Common/Button/FabButton";
import Fetching from "../../../Common/Fetching";
import ActivityForm from "./ActivityForm";

import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ActivityCard from "./ActivityCard";

function ActivityList({ activities, isFetching, onAdd, onUpdate, onDelete }) {
    const [openDialog, setOpenDialog] = useState(null);
    const [selected, setSelected] = useState(null);

    const handleOpenAddDialog = () => setOpenDialog("add");
    const handleOpenEditDialog = (activity = null) => {
        setSelected(activity);
        setOpenDialog("update");
    };
    const handleOpenDeleteDialog = (activity) => {
        setSelected(activity);
        setOpenDialog("delete");
    };
    const handleCloseDialog = useCallback(() => {
        setOpenDialog(null);
        setSelected(null);
    }, []);

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>

                {isFetching && (
                    <Grid item xs={12}>
                        <Fetching />
                    </Grid>
                )}

                {!isFetching &&
                    activities?.length > 0 &&
                    activities
                        .sort((a, b) =>
                            (a.dateStart instanceof Date
                                ? a.dateStart
                                : new Date(a.dateStart)) <
                            (b.dateStart instanceof Date
                                ? b.dateStart
                                : new Date(b.dateStart))
                                ? 1
                                : -1
                        )
                        .map((activity) => (
                            <Grid
                                item
                                xs={12}
                                sm={12}
                                md={12}
                                key={activity.id}
                            >
                                <ActivityCard
                                    key={activity.id}
                                    activity={activity}
                                    onEdit={() =>
                                        handleOpenEditDialog(activity)
                                    }
                                    onDelete={() =>
                                        handleOpenDeleteDialog(activity)
                                    }
                                />
                            </Grid>
                        ))}
            </Grid>

            <FabButton onClick={() => handleOpenAddDialog(null)} />

            {openDialog === "delete" && !!selected && (
                <ConfirmDialog
                    open={true}
                    title="Delete activity"
                    content={`The following activity will be deleted: ${selected.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => onDelete(selected.id)}
                    onCancel={handleCloseDialog}
                    onClose={handleCloseDialog}
                />
            )}

            {["add", "update"].includes(openDialog) && (
                <ActivityForm
                    open={true}
                    formTitle={
                        openDialog === "add" ? "Add activity" : "Edit activity"
                    }
                    data={openDialog === "update" && selected}
                    onSave={
                        openDialog === "add"
                            ? onAdd
                            : (params) => onUpdate(selected.id, params)
                    }
                    onClose={handleCloseDialog}
                    submitLabel={`${openDialog === "add" ? "Add" : "Save"}`}
                    scroll="paper"
                />
            )}
        </Container>
    );
}

const ActivityListContainer = () => {
    const dispatch = useDispatch();
    const appCode = useSelector(getRmsAppCode);
    const activities = useSelector((state) => getActivities(state));
    const isFetchingState = useSelector((state) =>
        isFetching(state, DataTypes.ACTIVITIES)
    );

    useEffect(() => {
        dispatch(rmsActions.getActivities(appCode));
    }, [dispatch, appCode]);

    return (
        <ActivityList
            activities={activities}
            isFetching={isFetchingState}
            onAdd={(params) =>
                dispatch(rmsActions.addActivity(appCode, params))
            }
            onUpdate={(id, params) =>
                dispatch(rmsActions.updateActivity(appCode, id, params))
            }
            onDelete={(id) => dispatch(rmsActions.deleteActivity(appCode, id))}
        />
    );
};

ActivityListContainer.propTypes = {
    getActivities: PropTypes.func,
    addActivity: PropTypes.func,
    updateActivity: PropTypes.func,
    deleteActivity: PropTypes.func,
    activities: PropTypes.array,
    isFetching: PropTypes.bool,
};

export default ActivityListContainer;
